/**
 * SightMap embed for /community-map (full integration only for unit list + filters).
 *
 * Flow:
 * 1. Server renders ENGRAIN_COMMUNITY_MAP_UNITS JSON + filter toolbar (CommunityEngrainMapPage).
 * 2. initCommunityMapSightMapEmbed loads iframe; on ready, wires floor changes and filter sync.
 * 3. resolveActiveFilterUnitIds applies toolbar filters, or ?floorplanId= units intersected with available ids in deep-link mode.
 * 4. applyCommunityMapUnitFilters pushes matches to SightMap embed, updates floor counts, renders unit list, syncs filter bubbles.
 */
(function initCommunityMapPage() {
    const engrainPanel = document.getElementById('community-map-engrain-panel');
    if (engrainPanel == null) {
        return;
    }

    const engrainFullIntegration = engrainPanel.dataset.engrainFullIntegration === 'true';
    const floorplanDeepLink = lookupSightMapFloorplanDeepLink(floorplanIdFromUrl());
    const deepLinkUnitIds =
        floorplanDeepLink != null && Array.isArray(floorplanDeepLink.sightMapUnitIds)
            ? new Set(floorplanDeepLink.sightMapUnitIds.map(String))
            : null;
    let floorplanDeepLinkDismissed = false;

    /** SightMap toggles a unit off when locateUnitById is called again for the same active unit. */
    let lastLocatedEngrainUnitId = null;

    /** Client-only filter for units on the active floor; does not affect counts or embed matches. */
    let activeFloorFilterQuery = '';

    const ENGRAIN_UNIT_SEARCH_TRACKED_SESSION_KEY = 'rv-tracked-engrain-unit-search';

    /**
     * Only track unit search GA4 event once per browser session
     */
    function trackApplyActiveFloorFilterEvent() {
        if (sessionStorage.getItem(ENGRAIN_UNIT_SEARCH_TRACKED_SESSION_KEY) === 'true') {
            return;
        }
        trackGA4Event('apply_engrain_unit_search');
        sessionStorage.setItem(ENGRAIN_UNIT_SEARCH_TRACKED_SESSION_KEY, 'true');
    }

    function isEngrainMobileLayout() {
        return typeof isMobilePage === 'function' && isMobilePage();
    }

    function getCommunityMapEngrainIntegration() {
        return document.querySelector('#community-map-engrain-panel .community-map-engrain-integration');
    }

    /**
     * Whether the SightMap iframe is visible for the current layout.
     * On desktop the iframe is always visible.
     *
     * On mobile with basic embed (no integration root), the iframe is always visible.
     * On mobile with full integration, visibility follows the map/list toggle state
     * (`community-map-engrain-integration--map-visible`).
     */
    function isCommunityMapEngrainMapVisible() {
        const communityMapEngrainIntegration = getCommunityMapEngrainIntegration();
        return (
            isDesktopPage() ||
            communityMapEngrainIntegration == null ||
            communityMapEngrainIntegration.classList.contains('community-map-engrain-integration--map-visible')
        );
    }

    /**
     * Switches mobile between map and list view for the Engrain integration.
     * Updates layout classes, toggle label/icons, and aria-hidden on the map iframe and unit list.
     */
    function setCommunityMapEngrainMobileView(isMapView) {
        const communityMapEngrainIntegration = getCommunityMapEngrainIntegration();
        const toggle = document.getElementById('community-map-engrain-map-list-toggle');
        const iframe = getSightMapIframe(engrainPanel);
        const unitListScroll = document.querySelector(
            '#community-map-engrain-panel .community-map-engrain-unit-list-scroll',
        );
        if (communityMapEngrainIntegration == null || toggle == null) {
            return;
        }
        communityMapEngrainIntegration.classList.toggle('community-map-engrain-integration--map-visible', isMapView);
        communityMapEngrainIntegration.classList.toggle('community-map-engrain-integration--list-visible', !isMapView);
        const buttonText = toggle.querySelector('.button-text');
        if (buttonText != null) {
            buttonText.textContent = isMapView ? 'List View' : 'Map View';
        }
        toggle
            .querySelector('.community-map-engrain-map-list-toggle-icon-list')
            ?.classList.toggle('hidden', !isMapView);
        toggle.querySelector('.community-map-engrain-map-list-toggle-icon-map')?.classList.toggle('hidden', isMapView);
        toggle.setAttribute('aria-pressed', isMapView ? 'false' : 'true');
        if (iframe != null) {
            iframe.setAttribute('aria-hidden', isMapView ? 'false' : 'true');
        }
        if (unitListScroll != null) {
            unitListScroll.setAttribute('aria-hidden', isMapView ? 'true' : 'false');
        }
    }

    function showCommunityMapEngrainMapView() {
        setCommunityMapEngrainMobileView(true);
    }

    /**
     * Reveals the Engrain integration UI once the SightMap embed is ready.
     * Mobile and desktop use different layouts, so each shows its own toolbar or sidebar region.
     */
    function revealCommunityMapEngrainIntegrationUi() {
        if (isEngrainMobileLayout()) {
            document.getElementById('community-map-engrain-mobile-toolbar')?.classList.remove('hidden');
            document.getElementById('community-map-engrain-active-floor-summary-slot')?.classList.remove('hidden');
            document.getElementById('community-map-engrain-unit-filter-slot')?.classList.remove('hidden');
        } else {
            document.querySelector('.community-map-engrain-unit-sidebar')?.classList.remove('hidden');
        }
    }

    function initCommunityMapEngrainMapListToggle() {
        if (!isEngrainMobileLayout()) {
            return;
        }
        const toggle = document.getElementById('community-map-engrain-map-list-toggle');
        if (toggle == null || toggle.dataset.engrainViewToggleWired === 'true') {
            return;
        }
        toggle.dataset.engrainViewToggleWired = 'true';
        setCommunityMapEngrainMobileView(true);
        toggle.addEventListener('click', function () {
            const buttonText = toggle.querySelector('.button-text')?.textContent?.trim();
            if (buttonText === 'Map View') {
                trackGA4Event('toggle_engrain_map_view');
            } else if (buttonText === 'List View') {
                trackGA4Event('toggle_engrain_unit_list');
            }
            setCommunityMapEngrainMobileView(!isCommunityMapEngrainMapVisible());
        });
    }

    /**
     * Restores "Your Matches" to every unit in the SightMap.
     * SightMap has no clear-matches call and treats an empty array as zero matches,
     * so the embed's own unit id set is the only way to undo a previous filter.
     */
    function resetEmbedUnitIdMatchesToAllUnits(embed) {
        if (typeof embed.getUnitIds !== 'function') {
            return;
        }
        embed.getUnitIds(function (unitIds) {
            try {
                embed.setUnitIdMatches((unitIds ?? []).map(String));
            } catch (e) {
                // SightMap iframe API not available or incompatible
            }
        });
    }

    /** Syncs which units in the SightMap with the active filters or deep-linked floorplan. */
    function pushEmbedUnitIdMatches(filterUnitIds) {
        const embed = window.communityMapSightMapEmbed;
        if (embed == null || typeof embed.setUnitIdMatches !== 'function') {
            return;
        }
        const activeFilterUnitIds = filterUnitIds ?? resolveActiveFilterUnitIds();
        const availableUnitIds = window.ENGRAIN_UNIT_AVAILABILITY?.availableUnitIds;
        if (activeFilterUnitIds == null && availableUnitIds == null) {
            resetEmbedUnitIdMatchesToAllUnits(embed);
            return;
        }
        const embedUnitIds =
            activeFilterUnitIds != null ? Array.from(activeFilterUnitIds) : [...availableUnitIds].map(String);
        try {
            embed.setUnitIdMatches(embedUnitIds);
        } catch (e) {
            // SightMap iframe API not available or incompatible
        }
    }

    function syncEngrainUnitCardSelection() {
        const selectedUnitId = lastLocatedEngrainUnitId;
        document
            .querySelectorAll('#community-map-engrain-unit-list .community-map-engrain-unit-card')
            .forEach(function (card) {
                const isSelected = selectedUnitId != null && card.dataset.engrainUnitId === selectedUnitId;
                card.classList.toggle('selected', isSelected);
            });
    }

    function locateEngrainUnitOnMap(engrainUnitId) {
        const embed = window.communityMapSightMapEmbed;
        const unitIdString = String(engrainUnitId);
        if (embed == null || typeof embed.locateUnitById !== 'function') {
            return;
        }
        if (lastLocatedEngrainUnitId === unitIdString) {
            return;
        }
        try {
            embed.locateUnitById(unitIdString);
            lastLocatedEngrainUnitId = unitIdString;
            syncEngrainUnitCardSelection();
        } catch (e) {
            // SightMap iframe API not available or incompatible
        }
    }

    /** Builds the SightMap embed URL from panel data attributes. */
    function buildSightMapEmbedUrl(panel) {
        if (panel == null) {
            return null;
        }
        const iframeKey = panel.getAttribute('data-engrain-iframe-key');
        if (iframeKey == null) {
            return null;
        }
        let url = `https://sightmap.com/embed/${iframeKey}?enable_api=1&origin=${window.location.protocol}//${window.location.host}`;
        const instanceKey = panel.getAttribute('data-engrain-instance-key');
        if (instanceKey) {
            url += `&instance=${instanceKey}`;
        }
        return url;
    }

    /** Returns the SightMap iframe for the given panel. */
    function getSightMapIframe(panel) {
        if (panel == null) {
            return null;
        }
        const iframeKey = panel.getAttribute('data-engrain-iframe-key');
        if (iframeKey == null) {
            return null;
        }
        return document.getElementById(`community-map-engrain-iframe-${iframeKey}`);
    }

    /** Reads a numeric floorplanId from the URL query string. */
    function floorplanIdFromUrl() {
        const floorplanId = new URLSearchParams(window.location.search).get('floorplanId');
        if (floorplanId == null || !/^\d+$/.test(floorplanId)) {
            return null;
        }
        return floorplanId;
    }

    /** Looks up a floorplan deep link from the server-rendered global lookup. */
    function lookupSightMapFloorplanDeepLink(floorplanId) {
        const floorplanIdsToEngrainUnitIds = window.FLOORPLAN_TO_UNIT_IDS;
        if (floorplanId == null || floorplanIdsToEngrainUnitIds == null) {
            return null;
        }
        const deepLink = floorplanIdsToEngrainUnitIds[floorplanId];
        if (deepLink == null || !Array.isArray(deepLink.sightMapUnitIds) || deepLink.sightMapUnitIds.length === 0) {
            return null;
        }
        return deepLink;
    }

    function getActiveFloorId() {
        const activeFloor = document.querySelector('#community-map-engrain-floors .community-map-engrain-floor.active');
        return activeFloor?.dataset.engrainFloorId ?? null;
    }

    function isValidEngrainFloorId(floorId) {
        return floorId != null && floorId !== '' && floorId !== 0 && floorId !== '0';
    }

    function isFloorplanDeepLinkMode() {
        return floorplanDeepLink != null && deepLinkUnitIds != null && !floorplanDeepLinkDismissed;
    }

    function getFilterToolbar() {
        return document.getElementById('floorplan-list-filters-container');
    }

    function hasToolbarFilterSelections() {
        const filterToolbar = getFilterToolbar();
        if (filterToolbar == null || window.RvFloorplanFilterUtils == null) {
            return false;
        }
        const filterState = window.RvFloorplanFilterUtils.getFilterStateFromToolbar(filterToolbar);
        return window.RvFloorplanFilterUtils.filterStateHasActiveSelections(filterState);
    }

    function resolveToolbarFilterUnitIds() {
        const allUnits = window.ENGRAIN_COMMUNITY_MAP_UNITS ?? [];
        const filterToolbar = getFilterToolbar();
        if (filterToolbar == null || window.RvFloorplanFilterUtils == null) {
            return null;
        }
        const filterState = window.RvFloorplanFilterUtils.getFilterStateFromToolbar(filterToolbar);
        if (!window.RvFloorplanFilterUtils.filterStateHasActiveSelections(filterState)) {
            return null;
        }
        return new Set(
            allUnits
                .filter(function (unit) {
                    return window.RvFloorplanFilterUtils.unitMatchesFilters(unit, filterState);
                })
                .map(function (unit) {
                    return String(unit.engrainUnitId);
                }),
        );
    }

    function setCommunityMapFilterUiVisible(visible) {
        getFilterToolbar()?.classList.toggle('hidden', !visible);
        const activeFiltersStrip = document.getElementById('floorplan-list-active-filters');
        if (activeFiltersStrip != null && !visible) {
            activeFiltersStrip.classList.add('hidden');
        }
    }

    function getCommunityPropertyTypePluralLabel() {
        const banner = document.getElementById('community-map-engrain-floorplan-banner');
        const label = banner?.dataset.communityPropertyTypePlural;
        if (label == null || label === '') {
            return 'Apartments';
        }
        return label;
    }

    function updateSeeAllAvailableLinkText() {
        const linkTextEl = document.getElementById('community-map-engrain-show-all-link-text');
        if (linkTextEl == null) {
            return;
        }
        const propertyTypePlural = getCommunityPropertyTypePluralLabel();
        if (hasToolbarFilterSelections()) {
            linkTextEl.textContent = 'See All Available ' + propertyTypePlural + ' Matching Your Filters';
            return;
        }
        linkTextEl.textContent = 'See All Available ' + propertyTypePlural;
    }

    /** Exits floorplan deep-link mode: clears URL param, hides banner, and restores normal filters. */
    function dismissFloorplanDeepLink() {
        if (!isFloorplanDeepLinkMode()) {
            return;
        }
        floorplanDeepLinkDismissed = true;

        const url = new URL(window.location.href);
        url.searchParams.delete('floorplanId');
        const nextUrl = url.search !== '' ? url.pathname + url.search + url.hash : url.pathname + url.hash;
        window.history.replaceState({}, '', nextUrl);

        document.getElementById('community-map-engrain-floorplan-banner')?.classList.add('hidden');
        setCommunityMapFilterUiVisible(true);
        applyCommunityMapUnitFilters();
    }

    /**
     * Dynamically wires the "See All Available" in the floorplan banner to dismiss the deep-link mode.
     */
    function wireSeeAllAvailableLink() {
        const link = document.getElementById('community-map-engrain-show-all-link');
        if (link == null || link.dataset.seeAllAvailableWired === 'true') {
            return;
        }
        link.dataset.seeAllAvailableWired = 'true';
        link.addEventListener('click', function (event) {
            event.preventDefault();
            dismissFloorplanDeepLink();
        });
    }

    function handleEngrainEmbedFloorChange(floorId) {
        if (!isValidEngrainFloorId(floorId)) {
            return;
        }
        const floorIdString = String(floorId);
        const activeFloorId = getActiveFloorId();
        if (activeFloorId != null && floorIdString === String(activeFloorId)) {
            return;
        }
        trackGA4Event('select_engrain_floor');
        clearActiveFloorFilter();
        setActiveEngrainFloor(floorIdString);
        updateActiveFloorSummary(floorIdString);
        renderEngrainUnitList(floorIdString, resolveActiveFilterUnitIds());
    }

    function getAvailableEngrainUnitIdsSet() {
        const availableUnitIds = window.ENGRAIN_UNIT_AVAILABILITY?.availableUnitIds;
        if (availableUnitIds == null) {
            return null;
        }
        return new Set(availableUnitIds.map(String));
    }

    /** Restricts a unit-id set to ids present in ENGRAIN_UNIT_AVAILABILITY (full integration only). */
    function intersectUnitIdsWithAvailable(unitIds) {
        if (unitIds == null) {
            return null;
        }
        const availableUnitIds = getAvailableEngrainUnitIdsSet();
        if (availableUnitIds == null) {
            return unitIds;
        }
        const intersected = new Set();
        for (const unitId of unitIds) {
            const unitIdString = String(unitId);
            if (availableUnitIds.has(unitIdString)) {
                intersected.add(unitIdString);
            }
        }
        return intersected;
    }

    /**
     * When we have a floorplan deep-link, only available units for that floorplan are shown
     * and toolbar filters are ignored until the user dismisses the banner.
     */
    function resolveActiveFilterUnitIds() {
        if (isFloorplanDeepLinkMode()) {
            return intersectUnitIdsWithAvailable(deepLinkUnitIds);
        }
        return resolveToolbarFilterUnitIds();
    }

    /**
     * Updates per-floor available unit counts for the current unit-match filter.
     * Pass null filterUnitIds for unfiltered totals (uses server-rendered availableCountByFloorId).
     */
    function updateEngrainFloorUnitCounts(filterUnitIds) {
        const availability = window.ENGRAIN_UNIT_AVAILABILITY;
        if (availability == null) {
            return;
        }
        if (filterUnitIds == null) {
            renderEngrainFloorUnitCounts(availability.availableCountByFloorId ?? {});
        } else {
            const unitIdToFloorId = availability.availableUnitIdToFloorId ?? {};
            const availableUnitIds = new Set((availability.availableUnitIds ?? []).map(String));
            const countsByFloorId = {};

            for (const unitId of filterUnitIds) {
                if (!availableUnitIds.has(String(unitId))) {
                    continue;
                }
                const floorId = unitIdToFloorId[String(unitId)];
                if (floorId != null) {
                    countsByFloorId[floorId] = (countsByFloorId[floorId] ?? 0) + 1;
                }
            }

            renderEngrainFloorUnitCounts(countsByFloorId);
        }

        const activeFloorId = getActiveFloorId();
        if (activeFloorId != null) {
            updateActiveFloorSummary(activeFloorId);
        }
        renderEngrainUnitList(activeFloorId, filterUnitIds);
    }

    /** Writes per-floor available unit counts into the custom floor selector. */
    function renderEngrainFloorUnitCounts(countsByFloorId) {
        document.querySelectorAll('.community-map-engrain-floor').forEach(function (floorItem) {
            const floorId = floorItem.dataset.engrainFloorId;
            const count = String(countsByFloorId[floorId] ?? 0);
            floorItem.dataset.availableUnitCount = count;
        });
    }

    /** Updates the active floor summary count and floor label. */
    function updateActiveFloorSummary(floorId) {
        const floorItem =
            (floorId != null ? document.getElementById('community-map-engrain-floor-' + floorId) : null) ??
            document.querySelector('#community-map-engrain-floors .community-map-engrain-floor.active');
        const availableUnitsEl = document.getElementById('community-map-engrain-active-floor-availability-count');
        const availableUnitsMobileEl = document.getElementById('community-map-engrain-mobile-unit-count');
        const floorLabelEl = document.getElementById('community-map-engrain-active-floor-name-label');
        if (floorItem == null || availableUnitsEl == null || floorLabelEl == null) {
            return;
        }

        const unitCount = Number.parseInt(floorItem.dataset.availableUnitCount ?? '0', 10);
        const propertyTypeLabel =
            unitCount === 1
                ? availableUnitsEl.dataset.communityPropertyTypeSingular
                : availableUnitsEl.dataset.communityPropertyTypePlural;
        availableUnitsEl.textContent = unitCount + ' Available ' + propertyTypeLabel;
        if (availableUnitsMobileEl != null) {
            availableUnitsMobileEl.textContent = unitCount + ' Available';
        }
        floorLabelEl.textContent = floorItem.dataset.engrainFloorShortLabel ?? '';
    }

    function appendHiddenIr(parent, text) {
        const hiddenIr = document.createElement('span');
        hiddenIr.className = 'hiddenIR';
        hiddenIr.textContent = text;
        parent.appendChild(hiddenIr);
    }

    function appendPerMonthLabel(parent) {
        const perMonth = document.createElement('span');
        perMonth.className = 'per-month-label';
        perMonth.textContent = '/month';
        parent.appendChild(perMonth);
    }

    function appendStrikethroughRentPrice(parent, unit) {
        const strike = document.createElement('span');
        strike.className = 'strikethrough';
        if (unit.rentPriceOverride) {
            strike.textContent = unit.rentPriceOverride;
        } else {
            strike.textContent = '$' + (unit.rentPriceLowPretty ?? '');
            if (unit.showRentRange && unit.rentPriceHighPretty) {
                appendHiddenIr(strike, 'to');
                const dash = document.createElement('span');
                dash.setAttribute('aria-hidden', 'true');
                dash.textContent = '-';
                strike.appendChild(dash);
                strike.appendChild(document.createTextNode('$' + unit.rentPriceHighPretty));
            }
        }
        parent.appendChild(strike);
    }

    /** Renders rent on unit cards and popups, including temporary pricing and strikethrough. */
    function populateUnitRentFields(rentInfoEl, rentEl, unit) {
        if (rentInfoEl == null || rentEl == null) {
            return;
        }
        rentInfoEl.innerHTML = '';
        rentEl.innerHTML = '';

        const showRentInfo = unit.rentPriceStartsWith != null || unit.hasTempPricing;
        if (showRentInfo) {
            rentInfoEl.classList.remove('hidden');
            if (unit.rentPriceStartsWith) {
                rentInfoEl.appendChild(document.createTextNode(unit.rentPriceStartsWith + '\u00a0'));
            }
            if (unit.hasTempPricing) {
                appendHiddenIr(rentInfoEl, 'Previous Price');
                appendStrikethroughRentPrice(rentInfoEl, unit);
            }
        } else {
            rentInfoEl.classList.add('hidden');
        }

        if (unit.hasTempPricing) {
            if (unit.temporaryPriceMinPretty) {
                rentEl.appendChild(document.createTextNode('$' + unit.temporaryPriceMinPretty));
            }
            if (!unit.rentPriceStartsWith && unit.temporaryPriceMaxPretty) {
                if (unit.temporaryPriceMinPretty) {
                    appendHiddenIr(rentEl, 'to');
                    const dash = document.createElement('span');
                    dash.setAttribute('aria-hidden', 'true');
                    dash.textContent = '-';
                    rentEl.appendChild(dash);
                }
                rentEl.appendChild(document.createTextNode('$' + unit.temporaryPriceMaxPretty));
            }
            appendPerMonthLabel(rentEl);
        } else if (unit.rentPriceOverride) {
            rentEl.appendChild(document.createTextNode(unit.rentPriceOverride));
            if (unit.rentPriceStartsWith) {
                appendPerMonthLabel(rentEl);
            }
        } else {
            rentEl.appendChild(document.createTextNode('$' + (unit.rentPriceLowPretty ?? '')));
            if (unit.showRentRange && unit.rentPriceHighPretty) {
                appendHiddenIr(rentEl, 'to');
                const dash = document.createElement('span');
                dash.setAttribute('aria-hidden', 'true');
                dash.textContent = '-';
                rentEl.appendChild(dash);
                rentEl.appendChild(document.createTextNode('$' + unit.rentPriceHighPretty));
            }
            appendPerMonthLabel(rentEl);
        }
    }

    /**
     * Populates the sqft information on a unit card and unit details popup.
     */
    function populateUnitSqftLabel(sqftEl, unit) {
        if (sqftEl == null) {
            return;
        }
        if (!unit.sqftPretty) {
            sqftEl.classList.add('hidden');
            sqftEl.textContent = '';
            return;
        }
        sqftEl.classList.remove('hidden');
        sqftEl.textContent = '';
        sqftEl.appendChild(document.createTextNode(unit.sqftPretty));
        if (unit.showSqftRange && unit.sqftMaxPretty) {
            appendHiddenIr(sqftEl, 'to');
            const dash = document.createElement('span');
            dash.setAttribute('aria-hidden', 'true');
            dash.textContent = '-';
            sqftEl.appendChild(dash);
            sqftEl.appendChild(document.createTextNode(unit.sqftMaxPretty));
        }
        const sqftAbbrev = document.createElement('span');
        sqftAbbrev.setAttribute('aria-hidden', 'true');
        sqftAbbrev.textContent = ' Sq. Ft.';
        sqftEl.appendChild(sqftAbbrev);
        appendHiddenIr(sqftEl, 'Square Feet');
    }

    function findEngrainCommunityMapUnit(engrainUnitId) {
        const units = window.ENGRAIN_COMMUNITY_MAP_UNITS ?? [];
        return units.find(function (unit) {
            return String(unit.engrainUnitId) === String(engrainUnitId);
        });
    }

    /** Fills in unit specific details into the Unit Details popup before Magnific opens it. */
    function populateUnitVcPopupDetails(popupEl, unit) {
        const availabilityDot = popupEl.querySelector('.unit-vc-popup-availability .availability-dot');
        const availabilityStatusEl = popupEl.querySelector('.unit-vc-popup-availability-status');
        if (availabilityDot != null) {
            availabilityDot.classList.toggle('available', !unit.displayIsWaitlist);
            availabilityDot.classList.toggle('signed-waitlist', Boolean(unit.displayIsWaitlist));
        }
        if (availabilityStatusEl != null) {
            availabilityStatusEl.textContent = unit.availabilityStatus ?? '';
        }

        const floorplanTitleEl = popupEl.querySelector('.unit-vc-popup-floorplan-title');
        if (floorplanTitleEl != null) {
            floorplanTitleEl.textContent = unit.floorplanTitle ?? '';
        }

        const unitNumberEl = popupEl.querySelector('.unit-vc-popup-unit-number');
        if (unitNumberEl != null) {
            unitNumberEl.textContent = unit.unitNumber ?? '';
        }

        const bedroomEl = popupEl.querySelector('.unit-vc-popup-bedroom-label');
        if (bedroomEl != null) {
            bedroomEl.textContent = unit.bedroomLabelShort ?? '';
        }

        const bathroomEl = popupEl.querySelector('.unit-vc-popup-bathroom-label');
        if (bathroomEl != null) {
            bathroomEl.textContent = (unit.prettyBathrooms ?? '') + ' Bath';
        }

        populateUnitSqftLabel(popupEl.querySelector('.unit-vc-popup-sqft-label'), unit);
        const sqftPill = popupEl.querySelector('.unit-vc-popup-sqft-pill');
        if (sqftPill != null) {
            sqftPill.classList.toggle('hidden', !unit.sqftPretty);
        }

        populateUnitRentFields(
            popupEl.querySelector('.unit-vc-popup-rent-info'),
            popupEl.querySelector('.unit-vc-popup-rent'),
            unit,
        );
        updateUnitVcPopupPrimaryCtas(popupEl, unit);
    }

    /** Shows eligible CTAs (max 2 for default order), restyles visible CTAs, and hides the container when empty. */
    function updateUnitVcPopupPrimaryCtas(popupEl, unit) {
        const ctaContainer = popupEl.querySelector('.unit-vc-popup-primary-ctas');
        const ctaAnchors = popupEl.querySelectorAll('.unit-vc-popup-primary-cta');
        const useDefaultCtaOrder = ctaContainer != null && ctaContainer.dataset.useDefaultCtaOrder === 'true';
        const visibleCtas = [];

        if (useDefaultCtaOrder) {
            const eligibleAnchors = [];
            ctaAnchors.forEach(function (anchor) {
                if (anchor.dataset.ctaType === 'apply-now') {
                    const hasApplyUrl = Boolean(unit.applyUrl);
                    if (hasApplyUrl) {
                        anchor.href = unit.applyUrl;
                        eligibleAnchors.push(anchor);
                    }
                    return;
                }
                eligibleAnchors.push(anchor);
            });

            ctaAnchors.forEach(function (anchor) {
                const show = eligibleAnchors.indexOf(anchor) >= 0 && eligibleAnchors.indexOf(anchor) < 2;
                anchor.classList.toggle('hidden', !show);
                if (show) {
                    visibleCtas.push(anchor);
                }
            });
        } else {
            ctaAnchors.forEach(function (anchor) {
                if (anchor.dataset.ctaType === 'apply-now') {
                    const hasApplyUrl = Boolean(unit.applyUrl);
                    anchor.classList.toggle('hidden', !hasApplyUrl);
                    if (hasApplyUrl) {
                        anchor.href = unit.applyUrl;
                    }
                }
                if (!anchor.classList.contains('hidden')) {
                    visibleCtas.push(anchor);
                }
            });
        }

        visibleCtas.forEach(function (anchor, index) {
            anchor.classList.remove('invertedAccentButton', 'accentButton');
            if (visibleCtas.length > 1 && index === 0) {
                anchor.classList.add('invertedAccentButton');
            } else {
                anchor.classList.add('accentButton');
            }
        });

        if (ctaContainer != null) {
            ctaContainer.classList.toggle('hidden', visibleCtas.length === 0);
        }
    }

    function unitVcPopupMagnificOpen(popupId, engrainUnitId) {
        const unit = findEngrainCommunityMapUnit(engrainUnitId);
        if (unit == null) {
            return;
        }
        const cleanPopupId = popupId.startsWith('#') ? popupId.substring(1) : popupId;
        const popupEl = document.getElementById(cleanPopupId);
        if (popupEl == null) {
            return;
        }
        popupEl.dataset.engrainUnitId = String(engrainUnitId);
        populateUnitVcPopupDetails(popupEl, unit);
        // Unit popup reuses the floorplan photo slider when the floorplan has VC assets
        if (popupEl.dataset.hasVisualContent === 'true') {
            floorplanVcPopupMagnificOpen(popupId);
        }
    }

    /** Page scroll when the map first opens a unit details popup; restored when the return stack is empty. */
    let mapUnitVcPopupPageScrollY = null;

    /** Opens the unit VC popup programmatically (e.g. from a SightMap map click). */
    function openEngrainUnitVcPopup(engrainUnitId) {
        const unit = findEngrainCommunityMapUnit(engrainUnitId);
        if (unit == null) {
            return;
        }
        const popupId = 'floorplan-' + unit.rentvisionFloorplanId + '-unit-vc-popup';
        if (document.getElementById(popupId) == null) {
            return;
        }
        $.magnificPopup.open({
            items: { src: '#' + popupId },
            type: 'inline',
            midClick: true,
            closeBtnInside: true,
            callbacks: {
                beforeOpen: function () {
                    if (mapUnitVcPopupPageScrollY == null) {
                        mapUnitVcPopupPageScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
                    }
                },
                open: function () {
                    unitVcPopupMagnificOpen('#' + popupId, engrainUnitId);
                },
                close: function () {
                    floorplanVcPopupMagnificClose('#' + popupId);
                },
            },
        });
    }

    window.restoreMapUnitVcPopupPageScrollIfDone = function () {
        if (mapUnitVcPopupPageScrollY == null) {
            return;
        }
        if ($.magnificPopup.instance?.isOpen) {
            return;
        }
        if (typeof hasMagnificPopupReturnState === 'function' && hasMagnificPopupReturnState()) {
            return;
        }
        const scrollY = mapUnitVcPopupPageScrollY;
        mapUnitVcPopupPageScrollY = null;
        window.scrollTo(0, scrollY);
    };

    /** Pushes return state so email/appointment popups reopen this unit details popup when dismissed. */
    function pushUnitVcPopupReturnStateIfOpen(popupEl) {
        if (typeof pushMagnificPopupReturnState !== 'function' || !$.magnificPopup.instance?.isOpen) {
            return;
        }
        const engrainUnitId = popupEl?.dataset?.engrainUnitId;
        if (engrainUnitId == null) {
            return;
        }
        pushMagnificPopupReturnState(function () {
            openEngrainUnitVcPopup(engrainUnitId);
        });
    }

    /** Wires See Details on a unit card to the shared Magnific + VC popup flow. */
    function wireUnitVcPopupSeeDetailsButton(seeDetailsBtn, unit) {
        const popupId = 'floorplan-' + unit.rentvisionFloorplanId + '-unit-vc-popup';
        const popupEl = document.getElementById(popupId);
        if (seeDetailsBtn == null || popupEl == null) {
            return;
        }
        seeDetailsBtn.href = '#' + popupId;
        seeDetailsBtn.dataset.engrainUnitId = unit.engrainUnitId;
        seeDetailsBtn.classList.add('open-popup-link-with-callbacks');
        seeDetailsBtn.dataset.popupOpenCallback = 'unitVcPopupMagnificOpenFromSeeDetails';
        seeDetailsBtn.dataset.popupCloseCallback = 'floorplanVcPopupMagnificClose';
        seeDetailsBtn.addEventListener('click', function (event) {
            // Card click also locates the unit on the map; don't trigger that from See Details
            event.stopPropagation();
            trackGA4Event('open_unit_details_popup_list');
        });
        seeDetailsBtn.addEventListener('keydown', function (event) {
            // prevent keyboard enter / space_bar on See Details button from propagating up to the unit card
            if (event.key === 'Enter' || event.key === ' ') {
                event.stopPropagation();
            }
        });
        initOpenPopupLinkWithCallbacks(seeDetailsBtn);
    }

    function populateUnitCard(card, unit) {
        const availabilityDot = card.querySelector('.availability-dot');
        const availabilityStatusEl = card.querySelector('.community-map-engrain-unit-card-availability-status');
        if (availabilityDot != null) {
            availabilityDot.classList.toggle('available', !unit.displayIsWaitlist);
            availabilityDot.classList.toggle('signed-waitlist', Boolean(unit.displayIsWaitlist));
        }
        if (availabilityStatusEl != null) {
            availabilityStatusEl.textContent = unit.availabilityStatus ?? '';
        }

        const titleEl = card.querySelector('.community-map-engrain-unit-card-title');
        if (titleEl != null) {
            titleEl.textContent = unit.unitDisplayTitle ?? '';
            titleEl.setAttribute('title', unit.unitDisplayTitle ?? '');
        }

        const bedroomEl = card.querySelector('.community-map-engrain-unit-card-bedroom-label');
        if (bedroomEl != null) {
            bedroomEl.textContent = unit.bedroomLabelShort ?? '';
        }

        const bathroomEl = card.querySelector('.community-map-engrain-unit-card-bathroom-label');
        if (bathroomEl != null) {
            bathroomEl.textContent = (unit.prettyBathrooms ?? '') + ' Bath';
        }

        const sqftEl = card.querySelector('.community-map-engrain-unit-card-sqft-label');
        populateUnitSqftLabel(sqftEl, unit);

        const rentInfoEl = card.querySelector('.community-map-engrain-unit-card-rent-info');
        const rentEl = card.querySelector('.community-map-engrain-unit-card-rent');
        populateUnitRentFields(rentInfoEl, rentEl, unit);

        card.dataset.engrainUnitId = unit.engrainUnitId;

        wireUnitVcPopupSeeDetailsButton(card.querySelector('.community-map-engrain-unit-card-see-details'), unit);
    }

    function getCommunityPropertyTypePluralLowercase() {
        const availableUnitsEl = document.getElementById('community-map-engrain-active-floor-availability-count');
        const propertyTypePlural = availableUnitsEl?.dataset.communityPropertyTypePlural;
        if (propertyTypePlural == null || propertyTypePlural === '') {
            return 'units';
        }
        return propertyTypePlural.toLowerCase();
    }

    function getEngrainUnitListEmptyMessage(filterUnitIds, hasUnitSearchQuery) {
        const propertyTypePlural = getCommunityPropertyTypePluralLowercase();
        if ((!isFloorplanDeepLinkMode() && hasToolbarFilterSelections()) || hasUnitSearchQuery) {
            return 'Sorry! No ' + propertyTypePlural + ' on this floor match those filters.';
        }
        return 'Sorry! No available ' + propertyTypePlural + ' on this floor.';
    }

    function getActiveFloorFilterInput() {
        return document.getElementById('community-map-engrain-active-floor-filter-input');
    }

    function getActiveFloorFilterClearButton() {
        return document.querySelector('#community-map-engrain-active-floor-filter button');
    }

    function updateActiveFloorFilterClearButtonVisibility() {
        const clearButton = getActiveFloorFilterClearButton();
        const searchInput = getActiveFloorFilterInput();
        if (clearButton == null || searchInput == null) {
            return;
        }
        clearButton.classList.toggle('hidden', searchInput.value.length === 0);
    }

    function clearActiveFloorFilter() {
        activeFloorFilterQuery = '';
        const searchInput = getActiveFloorFilterInput();
        if (searchInput != null) {
            searchInput.value = '';
        }
        updateActiveFloorFilterClearButtonVisibility();
    }

    function unitMatchesActiveFloorFilter(unit, query) {
        if (query === '') {
            return true;
        }
        const unitTitle = unit.unitDisplayTitle ?? '';
        return unitTitle.toLowerCase().includes(query);
    }

    function getFloorUnits(floorId, filterUnitIds) {
        const units = window.ENGRAIN_COMMUNITY_MAP_UNITS ?? [];
        const resolvedFloorId = floorId != null ? String(floorId) : null;

        return units.filter(function (unit) {
            if (resolvedFloorId != null && String(unit.floorId) !== resolvedFloorId) {
                return false;
            }
            if (filterUnitIds != null && !filterUnitIds.has(String(unit.engrainUnitId))) {
                return false;
            }
            return true;
        });
    }

    function updateActiveFloorFilterVisibility(floorId, filterUnitIds) {
        const searchContainer = document.getElementById('community-map-engrain-active-floor-filter');
        if (searchContainer == null) {
            return;
        }
        const hasUnitsOnFloor = getFloorUnits(floorId, filterUnitIds).length > 0;
        searchContainer.classList.toggle('hidden', !hasUnitsOnFloor);
    }

    /** Builds the sidebar unit list from the card template for the given floor and filters. */
    function renderEngrainUnitList(floorId, filterUnitIds) {
        const listEl = document.getElementById('community-map-engrain-unit-list');
        const emptyEl = document.getElementById('community-map-engrain-unit-list-empty');
        const emptyMessageEl = document.getElementById('community-map-engrain-unit-list-empty-message');
        const template = document.getElementById('community-map-engrain-unit-card-template');
        if (listEl == null || template == null) {
            return;
        }

        const normalizedSearchQuery = activeFloorFilterQuery.trim().toLowerCase();
        const floorUnits = getFloorUnits(floorId, filterUnitIds);
        const visibleUnits = floorUnits.filter(function (unit) {
            return unitMatchesActiveFloorFilter(unit, normalizedSearchQuery);
        });

        updateActiveFloorFilterVisibility(floorId, filterUnitIds);

        listEl.innerHTML = '';
        visibleUnits.forEach(function (unit) {
            const card = template.content.firstElementChild.cloneNode(true);
            populateUnitCard(card, unit);
            card.addEventListener('click', function (event) {
                if (event.target.closest('.community-map-engrain-unit-card-see-details')) {
                    return;
                }
                trackGA4Event('select_engrain_unit');
                locateEngrainUnitOnMap(unit.engrainUnitId);
                // From list view on mobile, switch to the map so the located unit is visible
                if (isEngrainMobileLayout() && !isCommunityMapEngrainMapVisible()) {
                    showCommunityMapEngrainMapView();
                }
            });
            card.addEventListener('keydown', function (event) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    card.click();
                }
            });
            listEl.appendChild(card);
        });

        syncEngrainUnitCardSelection();

        if (emptyEl != null) {
            emptyEl.classList.toggle('hidden', visibleUnits.length > 0);
        }
        if (emptyMessageEl != null && visibleUnits.length === 0) {
            emptyMessageEl.textContent = getEngrainUnitListEmptyMessage(filterUnitIds, normalizedSearchQuery !== '');
        }
    }

    /**
     * Central filter: updates map highlights, floor counts, unit list, and filter bubbles.
     * Skips toolbar persistence while the floorplan deep-link banner is active.
     */
    function applyCommunityMapUnitFilters() {
        const filterUnitIds = resolveActiveFilterUnitIds();
        pushEmbedUnitIdMatches(filterUnitIds);

        updateEngrainFloorUnitCounts(filterUnitIds);

        if (isFloorplanDeepLinkMode()) {
            return;
        }

        const filterToolbar = getFilterToolbar();
        if (filterToolbar != null && window.RvFloorplanFilterUtils != null) {
            window.RvFloorplanFilterUtils.saveFiltersToSessionStorage(filterToolbar);
            window.RvFloorplanFilterUtils.syncActiveFilterBubbles(filterToolbar);
        }
    }

    /** Sets the active floor button in the custom floor selector. */
    function setActiveEngrainFloor(floorId) {
        const floorsEl = document.getElementById('community-map-engrain-floors');
        if (floorsEl == null || floorId == null) {
            return;
        }
        const activeButton = floorsEl.querySelector('.community-map-engrain-floor.active');
        if (activeButton != null) {
            activeButton.classList.remove('active');
        }
        const activeFloor = document.getElementById('community-map-engrain-floor-' + floorId);
        activeFloor?.classList.add('active');
        if (activeFloor != null) {
            scrollHorizontalScrollContainerItemIntoView(floorsEl, activeFloor);
        }
        // Keep the single tab stop on the newly active floor after programmatic selection changes.
        refreshHorizontalScrollContainerKeyboard(floorsEl);
    }

    /** Sets the active floor button and reveals the floor selector after embed ready. */
    function initializeEngrainFloorSelector(activeFloorId) {
        const floorsEl = document.getElementById('community-map-engrain-floors');
        if (floorsEl == null) {
            return;
        }

        let floorId = activeFloorId;
        if (activeFloorId == null || activeFloorId === 0 || activeFloorId === '0') {
            // if we don't have a floor, or the returned id is 0, set the selected floor to the first floor
            const firstFloor = floorsEl.querySelector('.community-map-engrain-floor');
            firstFloor?.classList.add('active');
            floorId = firstFloor?.dataset.engrainFloorId ?? null;
        } else {
            // we have a floor id, set that one as active
            document.getElementById('community-map-engrain-floor-' + activeFloorId)?.classList.add('active');
        }

        showEngrainFloorSelector();
        applyCommunityMapUnitFilters();
        updateActiveFloorSummary(floorId);
    }

    /** Reveals the custom floor selector after the SightMap embed is ready. */
    function showEngrainFloorSelector() {
        const floorsEl = document.getElementById('community-map-engrain-floors');
        if (floorsEl != null) {
            floorsEl.classList.remove('hidden');
            // Native floor buttons handle Enter/Space via onclick, so onActivate is omitted.
            initializeHorizontalScrollContainerWithKeyboard(floorsEl, {
                itemSelector: '.community-map-engrain-floor button',
                getActiveItem: function () {
                    return floorsEl.querySelector('.community-map-engrain-floor.active button');
                },
            });
        }
    }

    /** Shows the floorplan deep-link banner with title and property type. */
    function showCommunityMapEngrainFloorplanBanner(deepLink) {
        const banner = document.getElementById('community-map-engrain-floorplan-banner');
        const titleElement = document.getElementById('community-map-engrain-floorplan-banner-title');
        const propertyTypeElement = document.getElementById('community-map-engrain-floorplan-banner-property-type');
        if (banner == null || titleElement == null || propertyTypeElement == null) {
            return;
        }
        titleElement.textContent = deepLink.floorplanTitle ?? '';
        propertyTypeElement.textContent = deepLink.propertyTypePluralName ?? '';
        updateSeeAllAvailableLinkText();
        wireSeeAllAvailableLink();
        banner.classList.remove('hidden');
    }

    function initFloorplanDeepLinkMode() {
        if (!isFloorplanDeepLinkMode()) {
            return;
        }
        setCommunityMapFilterUiVisible(false);
        updateSeeAllAvailableLinkText();
    }

    function initCommunityMapFilters() {
        const filterToolbar = document.getElementById('floorplan-list-filters-container');
        if (filterToolbar == null || !engrainFullIntegration || window.RvFloorplanFilterUtils == null) {
            return;
        }

        window.RvFloorplanFilterUtils.initFilterToolbar({
            filterToolbar: filterToolbar,
            onFiltersChanged: applyCommunityMapUnitFilters,
            restoreFromSession: true,
        });
    }

    /** Loads the SightMap iframe and applies any ?floorplanId= deep-link filter. */
    function initCommunityMapSightMapEmbed() {
        const iframe = getSightMapIframe(engrainPanel);
        const embedUrl = buildSightMapEmbedUrl(engrainPanel);

        if (iframe == null || embedUrl == null || typeof SightMap === 'undefined') {
            return;
        }
        iframe.setAttribute('src', embedUrl);
        const embed = new SightMap.Embed(iframe.id);

        if (floorplanDeepLink != null && isFloorplanDeepLinkMode()) {
            showCommunityMapEngrainFloorplanBanner(floorplanDeepLink);
        }

        embed.on('ready', function () {
            window.communityMapSightMapEmbed = embed;
            try {
                if (engrainFullIntegration) {
                    // if we are using the full Engrain SightMap integration, disable built in ui because we have implemented our own
                    embed.disableUI(['filters', 'unitList', 'floorSelection', 'unitToolTip', 'unitDetails']);
                    applyCommunityMapUnitFilters();

                    embed.on('floor.change', function (event) {
                        handleEngrainEmbedFloorChange(event?.id);
                    });

                    embed.on('unitDetails.show', function (event) {
                        const engrainUnitId = event?.data?.unit?.id;
                        if (engrainUnitId == null) {
                            return;
                        }
                        lastLocatedEngrainUnitId = String(engrainUnitId);
                        syncEngrainUnitCardSelection();
                        trackGA4Event('open_unit_details_popup_map');
                        openEngrainUnitVcPopup(engrainUnitId);
                    });

                    embed.getFloor((floor) => {
                        initializeEngrainFloorSelector(floor);
                        revealCommunityMapEngrainIntegrationUi();
                    });
                } else if (isFloorplanDeepLinkMode()) {
                    // The basic embed has no filter toolbar or unit list, so the deep link is the only filter.
                    pushEmbedUnitIdMatches(deepLinkUnitIds);
                }
            } catch (e) {
                // SightMap iframe API not available or incompatible
            }
        });
    }

    /**
     * Narrows the unit list on the active floor only; toolbar filters and map counts are unchanged.
     */
    function initActiveFloorFilter() {
        const searchInput = getActiveFloorFilterInput();
        const clearButton = getActiveFloorFilterClearButton();
        if (searchInput == null || searchInput.dataset.unitSearchWired === 'true') {
            return;
        }
        searchInput.dataset.unitSearchWired = 'true';
        searchInput.addEventListener('input', function () {
            activeFloorFilterQuery = searchInput.value;
            if (searchInput.value.trim() !== '') {
                trackApplyActiveFloorFilterEvent();
            }
            updateActiveFloorFilterClearButtonVisibility();
            const activeFloorId = getActiveFloorId();
            renderEngrainUnitList(activeFloorId, resolveActiveFilterUnitIds());
        });
        if (clearButton != null && clearButton.dataset.unitSearchClearWired !== 'true') {
            clearButton.dataset.unitSearchClearWired = 'true';
            clearButton.addEventListener('click', function () {
                if (searchInput.value.trim() !== '') {
                    trackGA4Event('clear_engrain_unit_search');
                }
                clearActiveFloorFilter();
                const activeFloorId = getActiveFloorId();
                renderEngrainUnitList(activeFloorId, resolveActiveFilterUnitIds());
            });
        }
    }

    function initUnitVcPopupSpecialTermsReturn() {
        // Capture phase: Magnific's own click handler on the link opens the terms popup
        // synchronously, so the return state must be pushed while the unit popup is still open.
        document.addEventListener(
            'click',
            function (event) {
                const termsLink = event.target?.closest?.('.predictive-special-terms-link');
                if (termsLink == null || document.querySelector(termsLink.getAttribute('href')) == null) {
                    return;
                }
                pushUnitVcPopupReturnStateIfOpen(termsLink.closest('.unit-vc-popup'));
            },
            true,
        );
    }

    function initCommunityMapPageScripts() {
        initCommunityMapFilters();
        initFloorplanDeepLinkMode();
        initActiveFloorFilter();
        initCommunityMapEngrainMapListToggle();
        initCommunityMapSightMapEmbed();
        initUnitVcPopupSpecialTermsReturn();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initCommunityMapPageScripts);
    } else {
        initCommunityMapPageScripts();
    }

    window.setCommunityMapEngrainActiveFloor = function (event) {
        const floorItem = event.target.closest('.community-map-engrain-floor');
        const floorId = floorItem.dataset.engrainFloorId;
        const activeFloorId = getActiveFloorId();
        if (activeFloorId == null || String(activeFloorId) !== String(floorId)) {
            trackGA4Event('select_engrain_floor');
        }
        clearActiveFloorFilter();
        window.communityMapSightMapEmbed.setFloor(floorId);
        setActiveEngrainFloor(floorId);
        updateActiveFloorSummary(floorId);
        renderEngrainUnitList(floorId, resolveActiveFilterUnitIds());
    };

    window.unitVcPopupMagnificOpenFromSeeDetails = function (popupId) {
        const trigger = $.magnificPopup.instance?.ev?.context;
        const engrainUnitId = trigger?.dataset?.engrainUnitId;
        if (engrainUnitId == null) {
            return;
        }
        unitVcPopupMagnificOpen(popupId, engrainUnitId);
    };

    window.unitVcPopupPrimaryCtaClick = function (event, anchor) {
        const popup = anchor.closest('.unit-vc-popup');
        const floorplanId = popup?.dataset.floorplanId;
        const floorplanTitle = popup?.dataset.floorplanTitle;
        const config = window.UNIT_VC_POPUP_CONTACT;

        switch (anchor.dataset.ctaType) {
            case 'email':
                pushUnitVcPopupReturnStateIfOpen(popup);
                loadEmail({
                    floorplanId,
                    communityName: config.communityName,
                    floorplanName: floorplanTitle,
                    commentPlaceholder: config.emailMessageContent,
                });
                if ($.magnificPopup.instance?.isOpen) {
                    resetMagnificPopupScrollPosition();
                }
                return true;
            case 'appointment':
                pushUnitVcPopupReturnStateIfOpen(popup);
                loadAppointment({
                    floorplanId,
                    communityName: config.communityName,
                    floorplanName: floorplanTitle,
                    hoursCoded: config.hoursCoded,
                    startTimeBufferInMinutes: config.startTimeBufferInMinutes,
                    openingBuffer: config.openingBuffer,
                    closingBuffer: config.closingBuffer,
                    commentPlaceholder: config.emailMessageContent,
                });
                if ($.magnificPopup.instance?.isOpen) {
                    resetMagnificPopupScrollPosition();
                }
                return true;
            case 'apply-now':
                event.preventDefault();
                trackEngagedAction(2);
                trackGA4Event('rv_cv_click_apply_units');
                const engrainUnitId = popup?.dataset?.engrainUnitId;
                const applyUnit = engrainUnitId != null ? findEngrainCommunityMapUnit(engrainUnitId) : null;
                if (applyUnit?.applyUrl) {
                    if (applyUnit.applyUrlExternal) {
                        window.open(applyUnit.applyUrl);
                    } else {
                        window.location = applyUnit.applyUrl;
                    }
                }
                return false;
            case 'appointment-link':
                trackGA4Event('rv_cv_click_appointment');
                return true;
            default:
                return true;
        }
    };

    window.unitVcPopupMagnificClose = function (popupId) {
        floorplanVcPopupMagnificClose(popupId);
    };
})();
